import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { t } from '../../i18n/nl.js';
import { getPushPreferences, savePushPreferences } from '../../utils/pushPreferences.js';

const CLIENT_PUSH_CATEGORIES = ['lessons_schedule', 'pt_appointments', 'billing', 'agreements', 'coach_messages'];
const PUSH_CAT_I18N_KEY = {
  lessons_schedule: 'lessonsSchedule',
  pt_appointments: 'ptAppointments',
  billing: 'billing',
  agreements: 'agreements',
  coach_messages: 'coachMessages',
};

export default function ClientInstellingenPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    customer_number: '',
    full_name: '',
    email: '',
    phone: '',
    street: '',
    house_number: '',
    postal_code: '',
    city: '',
  });
  const [pushPrefs, setPushPrefs] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  async function handleChangePassword(e) {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      showError(t('wachtwoordWijzigen.foutTeKort'));
      return;
    }
    if (newPassword !== confirmPassword) {
      showError(t('wachtwoordWijzigen.foutMismatch'));
      return;
    }
    setChangingPassword(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password: newPassword });
      if (err) throw err;
      setNewPassword('');
      setConfirmPassword('');
      showSuccess(t('wachtwoordWijzigen.succes'));
    } catch (err) {
      showError(t('wachtwoordWijzigen.fout') + err.message);
    } finally {
      setChangingPassword(false);
    }
  }

  useEffect(() => {
    if (!user?.id) return;
    getPushPreferences(supabase, user.id).then(setPushPrefs).catch(() => {});
  }, [user?.id]);

  async function handleTogglePushCat(key, checked) {
    const previous = pushPrefs;
    const next = { ...pushPrefs, [key]: checked };
    setPushPrefs(next);
    try {
      await savePushPreferences(supabase, user.id, next);
    } catch (e) {
      setPushPrefs(previous);
      showError(t('pushCat.foutOpslaan') + e.message);
    }
  }

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('profiles')
      .select('customer_number, full_name, email, phone, street, house_number, postal_code, city')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data, error: err }) => {
        if (err) { setError(t('clientInstellingen.foutLaden') + err.message); return; }
        if (data) setForm({
          customer_number: data.customer_number || '',
          full_name: data.full_name || '',
          email: data.email || '',
          phone: data.phone || '',
          street: data.street || '',
          house_number: data.house_number || '',
          postal_code: data.postal_code || '',
          city: data.city || '',
        });
        setLoading(false);
      });
  }, [user?.id]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const { error: updErr } = await supabase
        .from('profiles')
        .update({
          full_name: form.full_name,
          phone: form.phone || null,
          street: form.street || null,
          house_number: form.house_number || null,
          postal_code: form.postal_code || null,
          city: form.city || null,
        })
        .eq('id', user.id);
      if (updErr) throw updErr;
      showSuccess(t('clientInstellingen.opslaanSucces'));
    } catch (err) {
      setError(t('clientInstellingen.opslaanFout') + err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="loading-center" style={{ height: '60vh' }}><div className="spinner" /></div>;

  return (
    <div style={{ maxWidth: 600 }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('clientInstellingen.title')}</h1>
          <p className="page-subtitle">{t('clientInstellingen.subtitle')}</p>
        </div>
      </div>

      <div className="card">
        {error && <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">{t('clientInstellingen.labelKlantnummer')}</label>
            <input
              className="form-input"
              value={form.customer_number}
              readOnly
              style={{ background: 'var(--color-bg)', color: 'var(--color-text-muted)', cursor: 'default' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('clientInstellingen.labelVolledigeNaam')}</label>
            <input
              className="form-input"
              value={form.full_name}
              onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('clientInstellingen.labelEmail')}</label>
            <input
              className="form-input"
              value={form.email}
              readOnly
              style={{ background: 'var(--color-bg)', color: 'var(--color-text-muted)', cursor: 'default' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('clientInstellingen.labelTelefoon')}</label>
            <input
              className="form-input"
              value={form.phone}
              onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('clientInstellingen.labelStraat')}</label>
              <input
                className="form-input"
                value={form.street}
                onChange={e => setForm(f => ({ ...f, street: e.target.value }))}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('clientInstellingen.labelHuisnummer')}</label>
              <input
                className="form-input"
                value={form.house_number}
                onChange={e => setForm(f => ({ ...f, house_number: e.target.value }))}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginTop: 12 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('clientInstellingen.labelPostcode')}</label>
              <input
                className="form-input"
                value={form.postal_code}
                onChange={e => setForm(f => ({ ...f, postal_code: e.target.value }))}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">{t('clientInstellingen.labelWoonplaats')}</label>
              <input
                className="form-input"
                value={form.city}
                onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
              />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? '…' : t('clientInstellingen.opslaan')}
            </button>
          </div>
        </form>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
          {t('wachtwoordWijzigen.title')}
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
          {t('wachtwoordWijzigen.intro')}
        </p>
        <form onSubmit={handleChangePassword}>
          <div className="form-group">
            <label className="form-label">{t('wachtwoordWijzigen.labelNieuw')}</label>
            <input
              className="form-input"
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder={t('wachtwoordWijzigen.placeholderNieuw')}
              autoComplete="new-password"
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t('wachtwoordWijzigen.labelHerhaal')}</label>
            <input
              className="form-input"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="btn btn-primary" disabled={changingPassword}>
              {changingPassword ? t('auth.loading') : t('wachtwoordWijzigen.opslaanKnop')}
            </button>
          </div>
        </form>
      </div>

      <div className="card" style={{ marginTop: 24 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
          {t('pushCat.title')}
        </h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginBottom: 20, lineHeight: 1.5 }}>
          {t('pushCat.introClient')}
        </p>

        {pushPrefs && CLIENT_PUSH_CATEGORIES.map(key => {
          const i18nKey = PUSH_CAT_I18N_KEY[key];
          return (
            <div
              key={key}
              style={{ padding: '12px 16px', background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', marginBottom: 16 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  id={`pushcat_${key}`}
                  checked={pushPrefs[key] ?? true}
                  onChange={e => handleTogglePushCat(key, e.target.checked)}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                <label htmlFor={`pushcat_${key}`} className="form-label" style={{ marginBottom: 0, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
                  {t(`pushCat.${i18nKey}Label`)}
                </label>
              </div>
              <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 30, marginBottom: 0, lineHeight: 1.5 }}>
                {t(`pushCat.${i18nKey}Desc`)}
              </p>
            </div>
          );
        })}

        <p style={{ fontSize: 12, color: 'var(--color-text-muted)', fontStyle: 'italic', margin: 0 }}>
          {t('pushCat.alwaysOn')}
        </p>
      </div>
    </div>
  );
}
